<template>
  <div class="pg">
    <div class="pg-head">
      <h2><span class="mdi mdi-robot-happy-outline"></span> {{ $t('nav.chatbot') }}</h2>
      <p class="pg-sub">ถามภาษาคนได้เลย — ผู้ช่วยจะดึงตัวเลขจริงจากระบบมาสรุปให้ (เฟสนี้ดู/สรุป/นำทางเท่านั้น)</p>
    </div>

    <div class="cb-wrap">
      <!-- รายการบทสนทนา -->
      <aside class="cb-side">
        <button class="cb-new" @click="newChat"><span class="mdi mdi-plus"></span> เริ่มบทสนทนาใหม่</button>
        <div class="cb-convs">
          <div v-for="c in conversations" :key="c.conversationid" class="cb-conv-row" :class="{ on: c.conversationid === activeId }">
            <button class="cb-conv" @click="openConversation(c.conversationid)">
              <span class="cb-conv-title">{{ c.title || 'บทสนทนา' }}</span>
            </button>
            <button v-if="pendingDeleteId !== c.conversationid" class="cb-conv-del"
              v-hint="'ลบ'" aria-label="ลบบทสนทนานี้" @click.stop="pendingDeleteId = c.conversationid">
              <span class="mdi mdi-trash-can-outline"></span>
            </button>
            <span v-else class="cb-conv-confirm">
              <button class="cb-conv-yes" v-hint="'ยืนยันลบ'" aria-label="ยืนยันลบ" :disabled="deletingId === c.conversationid" @click.stop="doDelete(c.conversationid)"><span class="mdi mdi-check"></span></button>
              <button class="cb-conv-no" v-hint="'ยกเลิก'" aria-label="ยกเลิก" @click.stop="pendingDeleteId = null"><span class="mdi mdi-close"></span></button>
            </span>
          </div>
          <p v-if="!conversations.length" class="cb-empty">ยังไม่มีบทสนทนา</p>
        </div>
      </aside>

      <!-- ช่องแชท -->
      <section class="cb-main">
        <div ref="scroll" class="cb-msgs">
          <div v-if="!messages.length" class="cb-hello">
            <p><strong>ลองถามดูได้ เช่น</strong></p>
            <button v-for="s in samples" :key="s" class="cb-sample" @click="input = s; send()">{{ s }}</button>
          </div>
          <div v-for="(m, i) in messages" :key="i" class="cb-row" :class="m.role">
            <div class="cb-bubble" v-if="m.role === 'user'">{{ m.content }}</div>
            <div class="cb-bubble cb-bot" v-else v-html="formatMarkdown(m.content)"></div>
          </div>
          <div v-if="sending" class="cb-row assistant">
            <div class="cb-bubble cb-bot cb-typing">
              <span class="cb-dots" aria-hidden="true"><i></i><i></i><i></i></span>
              <span class="cb-typing-text">{{ thinkingText }}</span>
            </div>
          </div>
        </div>

        <div class="cb-input">
          <textarea v-model="input" rows="1" :disabled="sending" placeholder="พิมพ์คำถาม…"
            @keydown.enter.exact.prevent="send"></textarea>
          <button class="cb-send" :disabled="sending || !input.trim()" @click="send">
            <span class="mdi mdi-send"></span>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { sendChatMessage, listChatConversations, getChatConversation, deleteChatConversation } from './chatbotApi';
import { formatChatMarkdown, THINKING_STEPS } from '@/utils/chatMarkdown';

export default {
  name: 'ChatbotView',
  emits: ['onErrorHandler', 'onClickChangeState'],
  data() {
    return {
      conversations: [],
      activeId: null,
      pendingDeleteId: null,
      deletingId: null,
      messages: [],
      input: '',
      sending: false,
      // ข้อความระหว่างรอ — เปลี่ยนตามเวลาที่ผ่านไป ไม่ใช่ตามสถานะจริงของ backend (คำขอเดียวจบใน
      // HTTP รอบเดียว หน้าจอจึงไม่รู้ว่าตอนนั้นกำลังเรียก tool ตัวไหน) จึงเลือกคำที่ "จริงเสมอ"
      // ไม่ระบุชื่อ tool/ขั้นตอน — ถ้าอยากได้สถานะจริงต้องทำ SSE เป็นเฟสถัดไป
      thinkingStep: 0,
      thinkingTimer: null,
      samples: [
        'สัปดาห์นี้มีจองกี่ใบ',
        'ยอดขายเดือนนี้เท่าไร',
        'มีคอร์สไหนใกล้หมด/ค้างชำระบ้าง',
        'อยากดูรายงานการเงินต้องไปหน้าไหน',
      ],
    };
  },
  computed: {
    thinkingText() {
      return THINKING_STEPS[Math.min(this.thinkingStep, THINKING_STEPS.length - 1)].text;
    },
  },
  mounted() {
    this.loadConversations();
  },
  unmounted() {
    if (this.thinkingTimer) clearTimeout(this.thinkingTimer); // ออกจากหน้ากลางคันแล้ว timer ต้องไม่ค้าง
  },
  methods: {
    startThinking() {
      this.thinkingStep = 0;
      const startedAt = Date.now();
      const tick = () => {
        const next = THINKING_STEPS[this.thinkingStep + 1];
        if (!next) return; // ขั้นสุดท้ายค้างไว้ ไม่วนกลับ (วนกลับ = ดูเหมือนเริ่มใหม่)
        // `at` เป็นเวลาสะสมจากตอนกดส่ง — ต้องลบเวลาที่ผ่านไปแล้ว ไม่งั้นขั้นที่ 3-4 จะเลื่อนไปไกลกว่าที่ตั้งใจ
        const delay = Math.max(next.at - (Date.now() - startedAt), 0);
        this.thinkingTimer = setTimeout(() => { this.thinkingStep += 1; tick(); }, delay);
      };
      tick();
    },
    stopThinking() {
      if (this.thinkingTimer) { clearTimeout(this.thinkingTimer); this.thinkingTimer = null; }
      this.thinkingStep = 0;
    },
    async loadConversations() {
      try {
        const token = this.$store.getters.getToken;
        const data = await listChatConversations(this.baseURL, token);
        if (data && data.success) this.conversations = data.conversations || [];
      } catch (e) { /* เงียบ — รายการว่างไม่ใช่เรื่องร้ายแรง */ }
    },
    newChat() {
      this.activeId = null;
      this.messages = [];
      this.input = '';
    },
    async doDelete(id) {
      if (this.deletingId) return; // กันกดยืนยันซ้ำเร็วเกินกว่า Vue จะ re-render ซ่อนปุ่ม
      this.deletingId = id;
      this.pendingDeleteId = null;
      try {
        const token = this.$store.getters.getToken;
        await deleteChatConversation(this.baseURL, token, id);
        this.conversations = this.conversations.filter((c) => c.conversationid !== id);
        if (this.activeId === id) this.newChat();
      } catch (e) {
        this.$emit('onErrorHandler', 'ลบบทสนทนาไม่สำเร็จ');
      } finally {
        this.deletingId = null;
      }
    },
    async openConversation(id) {
      try {
        const token = this.$store.getters.getToken;
        const data = await getChatConversation(this.baseURL, token, id);
        if (data && data.success) {
          this.activeId = id;
          this.messages = (data.messages || []).map((m) => ({ role: m.role, content: m.content }));
          this.scrollDown();
        } else {
          this.$emit('onErrorHandler', (data && data.message) || 'เปิดบทสนทนาไม่สำเร็จ');
        }
      } catch (e) {
        this.$emit('onErrorHandler', 'เปิดบทสนทนาไม่สำเร็จ');
      }
    },
    async send() {
      const text = this.input.trim();
      if (!text || this.sending) return;
      this.messages.push({ role: 'user', content: text });
      this.input = '';
      this.sending = true;
      this.startThinking();
      this.scrollDown();
      try {
        const token = this.$store.getters.getToken;
        const data = await sendChatMessage(this.baseURL, token, { conversationId: this.activeId, text });
        if (data && data.success) {
          this.activeId = data.conversationId;
          this.messages.push({ role: 'assistant', content: data.reply });
          this.loadConversations(); // อัปเดตหัวข้อ/ลำดับ
        } else {
          this.messages.push({ role: 'assistant', content: (data && data.message) || 'ขออภัย ระบบขัดข้อง' });
        }
      } catch (e) {
        this.messages.push({ role: 'assistant', content: 'ขออภัย ระบบขัดข้องหรือใช้เวลานานเกินไป กรุณาลองใหม่' });
      } finally {
        this.stopThinking();
        this.sending = false;
        this.scrollDown();
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    formatMarkdown(text) { return formatChatMarkdown(text); },
  },
};
</script>

<style scoped>
.cb-wrap { display: grid; grid-template-columns: 240px 1fr; gap: 14px; height: calc(100vh - 180px); min-height: 420px; }
.cb-side { display: flex; flex-direction: column; gap: 10px; min-height: 0; }
.cb-new { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px;
  border-radius: var(--radius-lg); border: 1px solid var(--c-border);
  background: var(--c-surface); color: var(--c-text-body); cursor: pointer; font-weight: 600; }
.cb-new:hover { background: var(--c-surface-2); }
.cb-convs { overflow-y: auto; display: flex; flex-direction: column; gap: 4px; min-height: 0; }
.cb-conv-row { display: flex; align-items: center; gap: 2px; border-radius: 10px; border: 1px solid transparent; }
.cb-conv-row.on { background: var(--c-surface-2); border-color: var(--c-border); }
.cb-conv { flex: 1; min-width: 0; text-align: left; padding: 9px 10px; border-radius: 10px; border: none;
  background: transparent; color: var(--c-text-body); cursor: pointer; font-size: 0.9rem; }
.cb-conv:hover { background: var(--c-surface-2); }
.cb-conv-title { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cb-conv-del { background: transparent; border: none; color: var(--c-text-muted); cursor: pointer; padding: 6px; border-radius: 6px; flex-shrink: 0; }
.cb-conv-del:hover { background: var(--c-error-bg, rgba(220,38,38,0.12)); color: var(--c-error, #dc2626); }
.cb-conv-confirm { display: inline-flex; gap: 2px; flex-shrink: 0; padding-right: 6px; }
.cb-conv-yes, .cb-conv-no { background: transparent; border: none; cursor: pointer; padding: 6px; border-radius: 6px; }
.cb-conv-yes { color: var(--c-error, #dc2626); }
.cb-conv-yes:hover { background: var(--c-error-bg, rgba(220,38,38,0.12)); }
.cb-conv-no:hover { background: var(--c-surface-2); }
.cb-empty { color: var(--c-text-muted); font-size: 0.85rem; padding: 8px; }

.cb-main { display: flex; flex-direction: column; min-height: 0;
  border: 1px solid var(--c-border); border-radius: var(--radius-lg);
  background: var(--c-surface); overflow: hidden; }
.cb-msgs { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.cb-hello { color: var(--c-text-muted); display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
.cb-sample { text-align: left; padding: 8px 12px; border-radius: var(--radius-pill, 999px); border: 1px solid var(--c-border);
  background: var(--c-surface); color: var(--c-text-body); cursor: pointer; font-size: 0.88rem; }
.cb-sample:hover { background: var(--c-surface-2); }
.cb-row { display: flex; }
.cb-row.user { justify-content: flex-end; }
.cb-row.assistant { justify-content: flex-start; }
.cb-bubble { max-width: 80%; padding: 10px 14px; border-radius: 14px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
.cb-row.user .cb-bubble { background: var(--c-primary); color: var(--c-on-primary, #fff); border-bottom-right-radius: 4px; }
.cb-bot { background: var(--c-surface-2); color: var(--c-text-body); border-bottom-left-radius: 4px; white-space: normal; }
.cb-bot :deep(p) { margin: 0 0 6px; }
.cb-bot :deep(ul) { margin: 4px 0; padding-left: 18px; }
.cb-bot :deep(h4), .cb-bot :deep(h5) { margin: 8px 0 4px; }
/* .cb-typing / .cb-dots / .cb-table-wrap อยู่ใน istar-pages.css (ใช้ร่วมกับ ChatbotFab ที่ teleport ออกไป body) */
.cb-input { display: flex; gap: 8px; padding: 10px; border-top: 1px solid var(--c-border); }
.cb-input textarea { flex: 1; resize: none; border: 1px solid var(--c-border); border-radius: 10px;
  padding: 10px 12px; font: inherit; max-height: 120px; background: var(--c-surface); color: inherit; }
.cb-send { width: 44px; border: none; border-radius: 10px; background: var(--c-primary); color: var(--c-on-primary, #fff);
  cursor: pointer; font-size: 1.1rem; }
.cb-send:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 700px) {
  .cb-wrap { grid-template-columns: 1fr; height: calc(100vh - 160px); }
  .cb-side { display: none; }
}
</style>
